import { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, FileUp, ArrowRight } from "lucide-react";
import { useSite, phoneDisplay } from "../context/SiteContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/renovations", label: "Renovations" },
  { to: "/extensions", label: "Extensions" },
  { to: "/new-builds", label: "New Builds" },
  { to: "/projects", label: "Projects" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { phone } = useSite();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const goPlans = () => {
    setOpen(false);
    if (location.pathname === "/contact") {
      document.getElementById("upload")?.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      navigate("/contact#upload");
    }
  };

  const goUrgent = () => {
    setOpen(false);
    if (location.pathname === "/") {
      document.getElementById("urgent")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/#urgent");
    }
  };

  return (
    <>
      <header data-testid="site-header" className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${scrolled || open ? "bg-white/95 backdrop-blur-md border-b border-line" : "bg-white/90 backdrop-blur-sm border-b border-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid="header-logo" className="flex items-center gap-3 shrink-0" aria-label="Razor Build home">
            <img src="/logo-mark-black.png" alt="Razor Build logo" className="h-8 md:h-9 w-auto" />
            <span className="font-display font-bold text-lg md:text-xl tracking-tight text-ink">Razor<span>Build</span></span>
          </Link>

          <nav className="hidden xl:flex items-center gap-4 2xl:gap-6" aria-label="Main navigation">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `whitespace-nowrap font-mono text-[11px] tracking-[0.14em] uppercase transition-colors ${isActive ? "text-golddark" : "text-slate2 hover:text-ink"}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-2.5 shrink-0">
            <button onClick={goUrgent} data-testid="nav-urgent-btn" className="whitespace-nowrap inline-flex items-center gap-2 px-3 py-2.5 font-mono text-[11px] tracking-[0.12em] uppercase text-[#B3261E] hover:bg-[#B3261E] hover:text-white border border-[#B3261E]/40 hover:border-[#B3261E] transition-colors">
              <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" /> Urgent Help
            </button>
            <button onClick={goPlans} data-testid="nav-send-plans-btn" className="whitespace-nowrap group inline-flex items-center gap-2 border border-ink px-3.5 py-2.5 font-mono text-[11px] tracking-[0.12em] uppercase text-ink hover:bg-ink hover:text-white transition-colors">
              <FileUp className="w-3.5 h-3.5" /> Send Plans
            </button>
            <Link to="/contact" data-testid="nav-start-project-btn" className="whitespace-nowrap inline-flex items-center gap-2 bg-gold px-3.5 py-2.5 font-mono text-[11px] tracking-[0.12em] uppercase text-white hover:bg-golddark transition-colors">
              Start Your Project <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" className="xl:hidden p-2 text-ink" aria-label={open ? "Close menu" : "Open menu"}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div data-testid="mobile-menu" className="xl:hidden border-t border-line bg-white">
            <nav className="px-4 py-4 flex flex-col" aria-label="Mobile navigation">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) => `py-3 border-b border-line/60 font-display font-semibold text-lg ${isActive ? "text-golddark" : "text-ink"}`}
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="flex flex-col gap-3 pt-4 pb-2">
                <button onClick={goUrgent} data-testid="mobile-urgent-btn" className="inline-flex items-center justify-center gap-2 border border-[#B3261E]/50 px-4 py-3 font-mono text-xs tracking-[0.15em] uppercase text-[#B3261E]">
                  <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" /> Urgent Building Help
                </button>
                <button onClick={goPlans} data-testid="mobile-send-plans-btn" className="inline-flex items-center justify-center gap-2 border border-ink px-4 py-3 font-mono text-xs tracking-[0.15em] uppercase text-ink">
                  <FileUp className="w-4 h-4" /> Send Plans
                </button>
                <Link to="/contact" data-testid="mobile-start-project-btn" className="inline-flex items-center justify-center gap-2 bg-gold px-4 py-3 font-mono text-xs tracking-[0.15em] uppercase text-white">
                  Start Your Project <ArrowRight className="w-4 h-4" />
                </Link>
                <a href={`tel:${phone}`} data-testid="mobile-call-link" className="inline-flex items-center justify-center gap-2 text-slate2 font-mono text-xs tracking-wider py-1">
                  <Phone className="w-4 h-4" /> {phoneDisplay(phone)}
                </a>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
